import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import BdsCheckbox from '../../../../../../shared/components/v3/BdsCheckbox/BdsCheckbox';
import BdsInput from '../../../../../../shared/components/v3/BdsInput/BdsInput';
import BdsTooltip from '../../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import { CHECK_CIRCLE_V2, XERROR_ICON } from '../../../../../../shared/constants/images';
import { CLICK_ACTION, SPLIT_EQUAL_AMOUNT } from '../../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../../shared/context/plans/plansContext';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';
import { getRemainingCycles } from '../../../../../../shared/utils/flexibleRemuneration/payrollMonths';
import { toLocaleNumberFormat } from '../../../../../../shared/utils/utils';
import './PendingRequestPayments.scss';

interface IPendingRequestPayments {
  amount: number;
  currency: string;
  onApprove: (payments: { month: string; amount: number }[]) => void;
  onReject: () => void;
}
interface IMonthInput {
  label: string;
  amount: number | null;
}

const PendingRequestPayments = ({ amount, currency, onApprove, onReject }: IPendingRequestPayments) => {
  const [months, setMonths] = useState<{ [key: string]: IMonthInput }>({});
  const [isSplitEqually, setIsSplitEqually] = useState<boolean>(false);
  const { t } = useTranslation();
  const { locale: companyLocale, companyRFCycles } = useContext(CompanyContext);
  const { modelType } = useContext(PlansContext);

  const isFormValid = useCallback(() => {
    const total = Object.values(months).reduce((sum, month) => sum + (month.amount || 0), 0);
    return Math.abs(total - amount) < 0.005;
  }, [months, amount]);

  const areInputsEmpty = useCallback(() => {
    return Object.values(months).every((month) => month.amount === null);
  }, [months]);

  const generatedMonths = useMemo(
    () => getRemainingCycles({ companyRFCycles, companyLocale, modelType }),
    [companyRFCycles, companyLocale, modelType]
  );

  const processMonths = useCallback(() => {
    const dict: { [key: string]: IMonthInput } = {};
    generatedMonths.forEach((m) => {
      const key = `${m.year}-${String(m.index).padStart(2, '0')}`;
      dict[key] = { label: m.displayName, amount: null };
    });
    setMonths(dict);
  }, [generatedMonths]);

  const onChangeAmount = (monthKey: string, newAmount: number) => {
    setMonths((prevMonths) => ({
      ...prevMonths,
      [monthKey]: {
        ...prevMonths[monthKey],
        amount: newAmount
      }
    }));
  };

  const monthAmountInputFormatter = (value: string): string => {
    const cleaned = value.replaceAll(/[^\d,]/g, '');

    const firstComma = cleaned.indexOf(',');
    if (firstComma !== -1) {
      const intPart = cleaned.slice(0, firstComma);
      const decPart = cleaned
        .slice(firstComma + 1)
        .replaceAll(/,/g, '')
        .slice(0, 2);

      return decPart.length > 0 ? `${intPart},${decPart}` : `${intPart},`;
    }

    return cleaned;
  };

  const parseCommaDecimal = (raw: string): number => {
    const normalized = raw.replace(',', '.');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  const formatDisplayValue = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '';
    return new Intl.NumberFormat('es-ES', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(val);
  };

  const splitEqually = () => {
    const keys = Object.keys(months);
    const numMonths = keys.length;
    if (numMonths === 0) return;

    TagManagerService.pushEvent(CLICK_ACTION, SPLIT_EQUAL_AMOUNT, {
      value: !isSplitEqually
    });

    if (isSplitEqually) {
      setIsSplitEqually(false);
      return;
    }

    const totalCents = Math.round(amount * 100);
    const baseCents = Math.floor(totalCents / numMonths);
    let remainder = totalCents - baseCents * numMonths;
    const updated: { [key: string]: IMonthInput } = {};

    for (const k of keys) {
      const extra = remainder > 0 ? 1 : 0;
      if (remainder > 0) remainder -= 1;
      const cents = baseCents + extra;
      updated[k] = {
        ...months[k],
        amount: Number.parseFloat((cents / 100).toFixed(2))
      };
    }

    setMonths(updated);
    setIsSplitEqually(true);
  };

  const onApproveClick = () => {
    if (areInputsEmpty()) return;
    if (!isFormValid()) return;

    const payload = Object.entries(months)
      .filter(([, m]) => m.amount !== null && m.amount > 0)
      .map(([monthKey, m]) => ({ month: monthKey, amount: m.amount as number }));

    onApprove(payload);
  };

  useEffect(() => {
    processMonths();
  }, [processMonths]);

  useEffect(() => {
    if (!months || Object.keys(months).length === 0) {
      setIsSplitEqually(false);
      return;
    }
    const amounts = Object.values(months).map((m) => m.amount || 0);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    setIsSplitEqually(max - min <= 0.01 && isFormValid());
  }, [months, isFormValid]);

  return (
    <div className="flex flex-col gap-12">
      <div className="pending-request-payments">
        <div className="pending-request-payments-title">{t(TRANSLATIONS_REQUESTS.details.deductionsCard.title)}</div>
        <div className="pending-request-payments-content">
          <div className="payment-amount">
            <div className="label">{t(TRANSLATIONS_REQUESTS.details.deductionsCard.totalAmount)}</div>
            <div className="amount-value">
              <p className="font-bold">
                {toLocaleNumberFormat(
                  amount,
                  { style: 'currency', currency: currency, useGrouping: true, maximumFractionDigits: 2 },
                  'es-ES'
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-8">
            <div className="payment-split-checkbox">
              <div className="payment-split-label">
                {t(TRANSLATIONS_REQUESTS.details.deductionsCard.deductionPerPayroll)}
              </div>
              <div className="flex gap-3 items-center">
                <BdsCheckbox selected={isSplitEqually} onChange={splitEqually} value="" name="" className="p-0" />
                <div className="payment-split-description font-bold">
                  {t(TRANSLATIONS_REQUESTS.details.deductionsCard.equalSplitDescription)}
                </div>
              </div>
            </div>

            <div className="payments-inputs">
              {Object.keys(months).map((monthKey) => (
                <BdsInput
                  key={monthKey}
                  labelText={months[monthKey].label}
                  onChanged={(value) => onChangeAmount(monthKey, parseCommaDecimal(value))}
                  inputFormatters={[monthAmountInputFormatter]}
                  hintText={t(TRANSLATIONS_REQUESTS.details.deductionsCard.amountInputPlaceholder)}
                  initValue={formatDisplayValue(months[monthKey].amount)}
                  resetState={isSplitEqually}
                  className="month-payment-input"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-12 justify-end">
        <BdsButton variant="tertiary" size="m" action={onReject} iconLeft={XERROR_ICON}>
          {t(TRANSLATIONS_REQUESTS.details.deductionsCard.rejectButton)}
        </BdsButton>
        {isFormValid() ? (
          <BdsButton variant="primary" size="m" action={onApproveClick} iconLeft={CHECK_CIRCLE_V2}>
            {t(TRANSLATIONS_REQUESTS.details.deductionsCard.approveButton)}
          </BdsButton>
        ) : (
          <BdsTooltip
            message={
              areInputsEmpty()
                ? t(TRANSLATIONS_REQUESTS.details.deductionsCard.emptyInputsTooltip)
                : t(TRANSLATIONS_REQUESTS.details.deductionsCard.invalidAmountsTooltip)
            }
            position="top-center"
            variant="standard"
          >
            <BdsButton variant="primary" size="m" action={onApproveClick} iconLeft={CHECK_CIRCLE_V2} disabled>
              {t(TRANSLATIONS_REQUESTS.details.deductionsCard.approveButton)}
            </BdsButton>
          </BdsTooltip>
        )}
      </div>
    </div>
  );
};

export default PendingRequestPayments;
